import React, {
  useEffect,
  useMemo,
  useCallback,
  useRef,
  useState,
} from "react";
import _ from "lodash";
import { AlertComponent } from "../../common/components";
import { MUITable } from "../../common/components";
import { routes } from "../../constants/routes";
import { PaginationComponent } from "../../common/components";
import UnsubscribedUsersFilters from "./unsubscribed-users-filters";
import { getUnsubscribedUsersList } from "../../api/email-letter-templates";
import { convertUTCToLocal } from "../../utils/utils";
import { useSetLoading } from "../../common/hooks/appearance";
import UnsubscribedUsersFilterOptions from "./unsubscribed-users-filter-options";
import { unsubscribeUserFilterOptions } from "./email-letter-helper";
import './unsubscribed-users-list.scss'
interface UnsubscribedUsersListProps {
  setSelectedUserId: (id: number | null) => void;
  setSelectedUserType: (type: string) => void;
  setSelectedUserName: (name: string) => void;
  setViewOpen: (isOpen: boolean) => void;
  setCrumbMappingOption: any;
  setIndexValue: (index: number) => void;
}

const UnsubscribedUsersList: React.FC<UnsubscribedUsersListProps> = ({
  setSelectedUserId,
  setSelectedUserType,
  setSelectedUserName,
  setViewOpen,
  setCrumbMappingOption,
  setIndexValue,
}) => {

  const [searchText, setSearchText] = useState<string>("");
  const [selectedType, setSelectedType] = useState<string>("");
  const [isResetSearch, setIsResetSearch] = useState(false);
  const searchRef = useRef<{ clear: () => void }>(null);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(15);
  const [loading, setLoading] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [data, setData] = useState<any[]>([]);
  const [isShowConfirmationPopup, setIsShowConfirmationPopup] = useState(false);
  const [selectedUsersMap, setSelectedUsersMap] = useState<{
    [page: number]: number[];
  }>({});
  const isSearchTriggered = useRef(false);
  const [isOpenFilter, setIsOpenFilter] = useState<boolean>(false);
  const [selectedLeadsFilter, setSelectedLeadsFilters] = useState<number[]>([]);
  const [selectedLeadsFilterOptions, setSelectedLeadsFilterOptions] = useState<
    any[]
  >(unsubscribeUserFilterOptions);
  const [activeFilters, setActiveFilters] = useState<any>({});
  const [viewTaskvalue, setViewTaskvalue] = useState({
    open: false,
    taskId: null,
  });
  const [viewMeetingvalue, setViewMeetingvalue] = useState({
    open: false,
    meetingId: null,
  });
  const [viewCallValue, setViewCallValue] = useState({
    open: false,
    callId: null,
  });

  const isAllSelected = selectedUsersMap[currentPage]?.length === data.length;

  const isIndeterminate =
    selectedUsersMap[currentPage]?.length > 0 &&
    selectedUsersMap[currentPage]?.length < data.length;
  const handleSelectAll = () => {
    setSelectedUsersMap((prev) => {
      const currentPageLeads = data
        .filter((item) => item.lead_status !== "Closed-Won")
        .map((item) => item.id);
      const selectedLeadsOnPage = prev[currentPage] || [];

      if (selectedLeadsOnPage.length > 0) {
        return { ...prev, [currentPage]: [] };
      }
      return { ...prev, [currentPage]: currentPageLeads };
    });
  };

  const fetchUnsubscribedUsersList = async (
    page = 1,
    search: string = "",
    filters: any = {}
  ) => {
    setLoading(true);
    try {
      const offset = (page - 1) * pageSize;
      let params: any = {
        limit: pageSize,
        offset: offset,
      };
      if (search) {
        params.searchText = search;
      }
      if (filters?.userType) {
        params.userType = filters.userType?.length === 1 ? filters.userType[0] : 'both';
      }
      if (filters?.createdTimeStart && filters.createdTimeEnd) {
        // if (filters.createdTimeStart.isSame(filters.createdTimeEnd)) {
          params.optOutStart = filters.createdTimeStart.startOf("day");
          params.optOutEnd = filters.createdTimeEnd.endOf("day");
        // } else {
        //   params.optOutStart = filters.createdTimeStart;
        //   params.optOutEnd = filters.createdTimeEnd;
        // }
      }
      const response = await getUnsubscribedUsersList(params);
      if (response?.data?.success) {
        setData(response.data.data?.result || []);
        setTotalRecords(response.data.data?.totalRecords || 0);
      } else {
        setAlertMessage("Failed to fetch unsubscribed users list.");
        setAlertType("error");
        setAlertOpen(true);
      }
    } catch (error: any) {
      console.error("Error fetching unsubscribed users list:", error);
      setAlertMessage(
        error?.response?.data?.message ||
          "An error occurred while fetching the list."
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const pageToUse = isSearchTriggered.current ? 1 : currentPage;
    fetchUnsubscribedUsersList(pageToUse, searchText, activeFilters);
    isSearchTriggered.current = false;
  }, [currentPage, searchText, activeFilters]);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };

  const handleSearch = useCallback(
    _.debounce((text: string) => {
      setSearchText(text);
      isSearchTriggered.current = true;
      setCurrentPage(1);
      if (text === "") {
        setIsResetSearch(true);
        setTimeout(() => setIsResetSearch(false), 100);
      }
    }, 500),
    []
  );

  const resetSearch = () => {
    setSearchText("");
    setSelectedType("");
    if (searchRef.current) {
      searchRef.current.clear();
    }
    handleSearch("");
  };

  const headers = [
    {
      key: "name",
      label: "Name",
    },
    {
      key: "email",
      label: "Email",
    },
    {
      key: "phoneNumber",
      label: "Phone Number",
    },
    {
      key: "userType",
      label: "User Type",
    },
    // {
    //   key: "template",
    //   label: "Template",
    // },
    {
      key: "optOutDate",
      label: "Opt Out Date",
    },
  ];

  const fetchFullName = (item: any) => {
    const firstName = item?.first_name?.trim();
    const lastName = item?.last_name?.trim();
    const middleName = item?.middle_name ? item?.middle_name?.trim() : "";

    const fullName =
      firstName && lastName
        ? `${firstName} ${middleName} ${lastName}`
        : firstName
          ? firstName
          : middleName
            ? middleName
            : lastName
              ? lastName
              : "-";
    return fullName;
  };

  const formattedData = useMemo(() => {
    if (Array.isArray(data)) {
      return data.map((item) => {
        return {
          id: item.id,
          name: fetchFullName(item?.lead || item?.customer || {}),
          email: item?.email || "-",
          phoneNumber:
            item?.lead?.primary_phone_number ||
            item?.customer?.primary_phone_number ||
            "-",
          userType: item?.lead?.id ? "Lead" : item?.customer?.id ? "Customer" : "-",
          template: item?.template?.name || "-",
          optOutDate: item?.opt_out_date
            ? convertUTCToLocal(item?.opt_out_date, "MM-DD-YYYY hh:mm A")
            : "-",
          leadId: item?.lead?.id || null,
          customerId: item?.customer?.id || null,
        };
      });
    }
    return [];
  }, [data]);

  const noResultsText = useMemo(() => {
    if (loading) {
      return "Loading users...";
    }
    if (searchText) {
      return "No users found matching your search criteria";
    }
    return "No users found";
  }, [loading, searchText]);

  const handleCheckboxChange = (id: number) => {
    setSelectedUsersMap((prev) => {
      const prevSelected = prev[currentPage] || [];
      const newSelected = prevSelected?.includes(id)
        ? prevSelected?.filter((userId) => userId !== id)
        : [...prevSelected, id];

      return {
        ...prev,
        [currentPage]: newSelected,
      };
    });
  };
  const fetchUsersIncludingSearchAndFilters = () => {
    if (currentPage === 1) {
      fetchUnsubscribedUsersList(1, searchText || "", activeFilters);
    } else {
      setCurrentPage(1);
    }
  }
  const handleViewClick = async (item: any) => {
    if (item?.leadId || item?.customerId) {
      setSelectedUserId(item?.leadId || item?.customerId);
      setSelectedUserName(item?.name);
      setSelectedUserType(item?.userType.toLowerCase());
      setViewOpen(true);

      setCrumbMappingOption([
        {
          label: "Email Templates",
          link: routes.EMAILTEMPLATES,
          handleClick: (event: any) => {
            event.preventDefault();
            setViewOpen(false);
            setSelectedUserId(null);
            setSelectedUserType("");
            setSelectedUserName("");
            setIndexValue(0);
            setCrumbMappingOption([
              {
                label: "Email Templates",
                link: routes.EMAILTEMPLATES,
              },
            ]);
          },
        },
        {
          label: "Opt Out",
          handleClick: (event: any) => {
            event.preventDefault();
            // 👇 clear selected user and return to Opt Out tab
            setSelectedUserId(null);
            setSelectedUserName("");
            setSelectedUserType("");
            setViewOpen(false);
            setIndexValue(3); // Go back to Opt Out tab
            fetchUsersIncludingSearchAndFilters()
            setCrumbMappingOption([
              {
                label: "Email Templates",
                link: routes.EMAILTEMPLATES,
                handleClick: (e: any) => {
                  e.preventDefault();
                  setViewOpen(false);
                  setSelectedUserId(null);
                  setSelectedUserType("");
                  setSelectedUserName("");
                  setIndexValue(0);
                  setCrumbMappingOption([
                    {
                      label: "Email Templates",
                      link: routes.EMAILTEMPLATES,
                    },
                  ]);
                },
              },
              {
                label: "Opt Out",
                link: "#",
                handleClick: (e: any) => {
                  e.preventDefault();
                  setSelectedUserId(null);
                  setSelectedUserType("");
                  setSelectedUserName("");
                  setViewOpen(false);
                  setIndexValue(3);
                  fetchUsersIncludingSearchAndFilters();
                  setCrumbMappingOption([
                    {
                      label: "Email Templates",
                      link: routes.EMAILTEMPLATES,
                    },
                    {
                      label: "Opt Out",
                      link: "#",
                    },
                  ]);
                },
              },
            ]);
          },
        },
        {
          label: item?.name,
          link: "",
        },
      ]);
    }
  };

  const appliedFilterCount = useMemo(() => {
    let count = 0;
    const dateFilterActive =
      activeFilters.createdTimeStart || activeFilters.createdTimeEnd;
    const runFilterActive =
      activeFilters.lastRunStart || activeFilters.lastRunEnd;

    const otherFilters = { ...activeFilters };
    delete otherFilters.createdTimeStart;
    delete otherFilters.createdTimeEnd;
    delete otherFilters.lastRunStart;
    delete otherFilters.lastRunEnd;

    const otherActiveFilters = Object.values(otherFilters).filter(
      (val) =>
        val !== undefined &&
        val !== "" &&
        val !== null &&
        !(Array.isArray(val) && val.length === 0)
    ).length;

    if (dateFilterActive) {
      count += 1;
    }
    if (runFilterActive) {
      count += 1;
    }

    count += otherActiveFilters;

    return count;
  }, [activeFilters]);

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  return (
    <>
      <div className="unsubscribed-users-wrapper leadlist-wrapper">
       <h2 className="opt-out-title">Opt Out List</h2>
          <div>
            <UnsubscribedUsersFilters
              onSearch={handleSearch}
              isResetSearch={isResetSearch}
              searchRef={searchRef}
              isOpenFilter={isOpenFilter}
              setIsOpenFilter={setIsOpenFilter}
              selectedLeadsFilter={appliedFilterCount}
            />
          </div>
          <div className="lead-list-table-filter">
            <div className="lead-list-table-container">
              <div className="lead-list-table">
                <MUITable
                  headers={headers}
                  data={formattedData.map((item: any) => ({
                    key: item?.id,
                    name: item.name,
                    email: item.email,
                    phoneNumber: item.phoneNumber,
                    userType: item.userType,
                    // template: item.template,
                    optOutDate: item.optOutDate,
                    leadId: item.leadId,
                    customerId: item.customerId,
                    disableClick: item.name === "-" && item.userType === "-",
                  }))}
                  noResultsTxt={noResultsText}
                  onRowClick={(rowData: any) => handleViewClick(rowData)}
                  rowWise={true}
                />
              </div>
              <div
                style={{ display: isOpenFilter ? "block" : "none" , zIndex: 7}}
                className="lead-list-filter"
              >
                <UnsubscribedUsersFilterOptions
                  isOpenFilter={isOpenFilter}
                  setIsOpenFilter={setIsOpenFilter}
                  selectedLeadsFilter={selectedLeadsFilter}
                  setSelectedLeadsFilters={setSelectedLeadsFilters}
                  selectedLeadsFilterOptions={selectedLeadsFilterOptions}
                  setAlertMessage={setAlertMessage}
                  setAlertType={setAlertType}
                  setAlertOpen={setAlertOpen}
                  setSelectedLeadsFilterOptions={
                    setSelectedLeadsFilterOptions
                  }
                  onApplyFilters={(filters: any) => {
                    setActiveFilters(filters);
                    setCurrentPage(1);
                  }}
                  activeFilters={activeFilters}
                />
              </div>
            </div>
          </div>

          {totalRecords && totalRecords > 0 ? (
            <div className="leads-pagination-container">
              <PaginationComponent
                totalRecords={totalRecords}
                pageSize={pageSize}
                currentPage={currentPage}
                onPageChange={handlePageChange}
              />
            </div>
          ) : (
            <></>
          )}
        </div>
      <AlertComponent
        message={alertMessage}
        open={alertOpen}
        type={alertType}
        onClose={() => {
          setAlertOpen(false);
        }}
      />
    </>
  );
};

export default UnsubscribedUsersList;
